import { AlertTriangle } from 'lucide-react';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirm_label: string;
  cancel_label: string;
  loading_label?: string;
  loading?: boolean;
  destructive?: boolean;
  on_confirm: () => void | Promise<void>;
  on_cancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirm_label,
  cancel_label,
  loading_label,
  loading = false,
  destructive = false,
  on_confirm,
  on_cancel,
}: ConfirmDialogProps): JSX.Element | null {
  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'mt-0.5 rounded-md p-2',
                destructive
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-primary/10 text-primary',
              )}
            >
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </div>
            <div className="space-y-1">
              <CardTitle id="confirm-dialog-title" className="text-base">
                {title}
              </CardTitle>
              <p id="confirm-dialog-description" className="text-sm text-muted-foreground">
                {description}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_cancel} disabled={loading}>
            {cancel_label}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => {
              void on_confirm();
            }}
            disabled={loading}
          >
            {loading ? (loading_label ?? confirm_label) : confirm_label}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
